import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, TrendingUp, BookOpen, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { insights as mockInsights } from '../data/mock';

const FeaturedInsights = () => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch insights from CMS
  useEffect(() => {
    const fetchInsights = async () => {
      try {
        const response = await fetch(`${API_URL}/api/content/insights?limit=4`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setInsights(data);
          } else {
            // Fall back to mock data
            setInsights(mockInsights.slice(0, 4));
          }
        } else {
          setInsights(mockInsights.slice(0, 4));
        }
      } catch (err) {
        setInsights(mockInsights.slice(0, 4));
      } finally {
        setLoading(false);
      }
    };

    fetchInsights();
  }, [API_URL]);

  // Get featured article and secondary articles
  const featuredArticle = insights[0];
  const secondaryArticles = insights.slice(1, 4);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Category colors
  const getCategoryColor = (category) => {
    const colors = {
      'Market Research': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'Market Analysis': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'Investment Strategy': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      'Tax & Regulatory': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      'Industry News': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      'Fund Updates': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      'default': 'bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30'
    };
    return colors[category] || colors.default;
  };

  // Estimate read time
  const getReadTime = (content) => {
    if (!content) return '5 min read';
    const wordCount = content.split(/\s+/).length;
    const minutes = Math.ceil(wordCount / 200);
    return `${minutes} min read`;
  };

  if (loading || insights.length === 0) {
    return null;
  }

  return (
    <section className="py-20 lg:py-28 relative overflow-hidden bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#D4AF37]/3 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none" />
      
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 mb-6">
            <BookOpen className="h-4 w-4 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-sm font-medium">Research & Insights</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Expert Market Analysis
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Data-driven insights and research on Texas real estate markets to help inform your investment decisions
          </p>
        </motion.div>

        {/* Insights Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid lg:grid-cols-12 gap-6"
        >
          {/* Featured Article */}
          {featuredArticle && (
            <motion.div
              variants={itemVariants}
              className="lg:col-span-7"
            >
              <Link 
                to={`/insights/${featuredArticle.slug || featuredArticle.id}`}
                className="group block h-full"
                data-testid="homepage-featured-insight"
              >
                <div className="relative h-full rounded-2xl overflow-hidden bg-gradient-to-br from-[#111D35] to-[#0A1628] border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300">
                  {/* Image */}
                  <div className="relative h-64 lg:h-80 overflow-hidden">
                    {featuredArticle.featured_image || featuredArticle.image ? (
                      <img 
                        src={featuredArticle.featured_image || featuredArticle.image} 
                        alt={featuredArticle.title}
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#D4AF37]/20 to-transparent flex items-center justify-center">
                        <TrendingUp className="h-16 w-16 text-[#D4AF37]/30" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/60 to-transparent" />
                  </div>

                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(featuredArticle.category)}`}>
                        {featuredArticle.category}
                      </span>
                      <span className="px-3 py-1 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-medium border border-[#D4AF37]/30">
                        Featured
                      </span>
                    </div>
                    <h3 className="text-xl lg:text-2xl font-bold text-white mb-3 group-hover:text-[#D4AF37] transition-colors line-clamp-2">
                      {featuredArticle.title}
                    </h3>
                    <p className="text-white/60 mb-4 line-clamp-2">
                      {featuredArticle.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-white/40 text-sm">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {formatDate(featuredArticle.publish_date || featuredArticle.date || featuredArticle.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {featuredArticle.readTime || getReadTime(featuredArticle.content)}
                        </span>
                      </div>
                      <span className="flex items-center gap-1 text-[#D4AF37] font-medium group-hover:gap-2 transition-all">
                        Read <ChevronRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          )}

          {/* Secondary Articles */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            {secondaryArticles.map((article, index) => (
              <motion.div
                key={article.id}
                variants={itemVariants}
              >
                <Link 
                  to={`/insights/${article.slug || article.id}`}
                  className="group block"
                  data-testid={`homepage-insight-${index}`}
                >
                  <div className="flex gap-4 p-4 rounded-xl bg-gradient-to-br from-[#111D35] to-[#0A1628] border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300">
                    {/* Thumbnail */}
                    <div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden">
                      {article.featured_image || article.image ? (
                        <img 
                          src={article.featured_image || article.image} 
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#D4AF37]/20 to-transparent flex items-center justify-center">
                          <BookOpen className="h-6 w-6 text-[#D4AF37]/30" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border mb-2 ${getCategoryColor(article.category)}`}>
                        {article.category}
                      </span>
                      <h4 className="text-white font-semibold mb-1 group-hover:text-[#D4AF37] transition-colors line-clamp-2 text-sm">
                        {article.title}
                      </h4>
                      <div className="flex items-center gap-3 text-white/40 text-xs">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(article.publish_date || article.date || article.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* View All CTA */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Link to="/insights">
            <Button 
              variant="outline" 
              className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 group"
              data-testid="view-all-insights-btn"
            >
              View All Insights
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedInsights;
